'use client';

import Alert from '@legacy/styled-components/components/Alert';
import Container from '@legacy/styled-components/components/Container';
import Loader from '@legacy/styled-components/components/Loader';
import { PageSection } from '@legacy/styled-components/components/PageSection';
import PageHeader from '@legacy/styled-components/structures/PageHeader';
import Cookies from 'cookies-js';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const FailedFetchDynamicImportedModuleChrome = 'Failed to fetch dynamically imported module:';
const FailedFetchDynamicImportedModuleIos = 'Importing a module script failed';

const isDynamicImportModuleError = (error: Error) => {
  const message = error.message;
  if (
    message?.startsWith(FailedFetchDynamicImportedModuleChrome) ||
    message?.startsWith(FailedFetchDynamicImportedModuleIos)
  ) {
    const retries = Number.parseInt(Cookies.get('w-retries') || '0', 10) || 0;
    return { error: true, canReload: retries < 1, currentRetries: retries };
  }
  return { error: false };
};

interface ErrorPageProps {
  error: Error & { digest?: string };
  reset: () => void;
}

export default function ErrorPage({ error, reset }: ErrorPageProps) {
  const { t } = useTranslation(['activity', 'other']);

  // biome-ignore lint/suspicious/noConsole: Log error for debugging purposes
  console.log(error);

  const dynamicError = isDynamicImportModuleError(error);

  useEffect(() => {
    if (dynamicError.error && dynamicError.canReload) {
      Cookies.set('w-retries', (dynamicError.currentRetries + 1).toString(), {
        expires: new Date(new Date().getTime() + 10 * 1000),
      });
      window.location.reload();
    }
  }, [dynamicError]);

  return (
    <Container>
      <PageHeader
        heading={t('pageError.heading', { ns: 'other' })}
        preamble={<p>{t('pageError.description', { ns: 'other' })}</p>}
        breadcrumbs={[
          {
            displayName: t('activity', { ns: 'activity' }),
            link: {
              to: '/',
              title: t('activity', { ns: 'activity' }),
            },
          },
          {
            displayName: t('pageError.heading', { ns: 'other' }),
          },
        ]}
      />
      <PageSection>
        {dynamicError.error && dynamicError.canReload ? (
          <Loader />
        ) : (
          <Alert
            tone="error"
            heading={t('pageError.alertHeading', { ns: 'other' }) || undefined}
            message={error.message || t('pageError.alertMessageUnknown', { ns: 'other' })}
          />
        )}
      </PageSection>
    </Container>
  );
}
